import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, TextField, MenuItem, IconButton, Tooltip } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import DataTable from 'react-data-table-component';
import { formatDouble, long } from '../../components/utils';
const ReactHighcharts = require('react-highcharts');

const BUCKET_COLORS = ['#4fc3f7', '#ffb74d', '#81c784', '#ce93d8', '#ff8a65', '#aed581', '#f06292', '#4dd0e1'];
const CONTROL_COLOR = '#90a4ae';
const BORDER_COLOR = '#666666';
const FONT_FAMILY = 'Roboto, Helvetica, Arial, sans-serif';
const SEPARATOR = <div style={{ height: '15px', backgroundColor: 'transparent' }} />;

const getGroupColor = (group, bucketIndex) => {
  if (group === 'control') return CONTROL_COLOR;
  if (group === 'test') return '#4fc3f7';
  return BUCKET_COLORS[bucketIndex % BUCKET_COLORS.length];
};

/**
 * ExperimentReportComponent renders experiment performance data using the
 * experiment_impression_report endpoint which queries impressions directly
 * with arrayJoin on the expg wire format for experiment metrics.
 */
const AGG_METRICS = [
  { value: 'rpmAR', label: 'RPM-AR', format: '$', decimals: 2, compute: (d) => d.ad_requests > 0 ? 1000000 * d.revenue / d.ad_requests : 0 },
  { value: 'rpmBR', label: 'RPM-BR', format: '$', decimals: 2, compute: (d) => d.bid_requests > 0 ? 1000000 * d.revenue / d.bid_requests : 0 },
  { value: 'cpm', label: 'CPM', format: '$', decimals: 2, compute: (d) => d.impressions > 0 ? 1000 * d.revenue / d.impressions : 0 },
  { value: 'revenue', label: 'Revenue', format: '$', decimals: 2, compute: (d) => d.revenue || 0 },
  { value: 'ad_requests', label: 'Ad Requests', format: '', decimals: 0, compute: (d) => d.ad_requests || 0 },
  { value: 'bid_requests', label: 'Bid Requests', format: '', decimals: 0, compute: (d) => d.bid_requests || 0 },
  { value: 'bid_responses', label: 'Bid Responses', format: '', decimals: 0, compute: (d) => d.bid_responses || 0 },
  { value: 'impressions', label: 'Impressions', format: '', decimals: 0, compute: (d) => d.impressions || 0 },
  { value: 'response_rate', label: 'Response Rate', format: '', decimals: 2, suffix: '%', compute: (d) => d.bid_requests > 0 ? 100 * d.bid_responses / d.bid_requests : 0 },
  { value: 'fill_rate', label: 'Fill Rate', format: '', decimals: 2, suffix: '%', compute: (d) => d.ad_requests > 0 ? 100 * d.impressions / d.ad_requests : 0 },
  { value: 'requests_rate', label: 'Requests Rate', format: '', decimals: 2, suffix: '%', compute: (d) => d.bid_requests > 0 ? 100 * d.impressions / d.bid_requests : 0 }
];

const liftColor = (lift) => lift >= 0.02 ? '#81c784' : lift <= -0.02 ? '#e57373' : '#ffb74d';
const liftLabel = (lift) => `${lift >= 0 ? '+' : ''}${(lift * 100).toFixed(2)}%`;

const metricLiftColor = (row, controlRow, getValue) => {
  if (!controlRow || row.name === 'control') return 'inherit';
  const val = getValue(row);
  const controlVal = getValue(controlRow);
  if (controlVal === 0) return 'inherit';
  return liftColor((val - controlVal) / controlVal);
};

const TEST_COLOR = '#4fc3f7';
const EXPLORATION_COLOR = '#ffb74d';

const conditionalRowStyles = [
  { when: row => row.name === 'control', style: { backgroundColor: 'rgba(144,164,174,0.1)' } },
  { when: row => row.name === 'test', style: { backgroundColor: 'rgba(79,195,247,0.1)' } },
  { when: row => row.name && row.name.startsWith('bucket_'), style: { backgroundColor: 'rgba(255,183,77,0.05)' } }
];

const ExperimentReportComponent = ({ authProvider, dateRange = 7, startDate, endDate, ...props }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [selectedExperimentId, setSelectedExperimentId] = useState('');
  const [summary, setSummary] = useState(null);
  const [aggMetric, setAggMetric] = useState('rpmBR');
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch available experiments list
  useEffect(() => {
    const fetchExperiments = async () => {
      try {
        const baseUrl = `${window.nobid_endpoint}/experiments`;
        const token = localStorage.getItem('token');
        const response = await fetch(baseUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const result = await response.json();
        if (result && Array.isArray(result)) {
          setExperiments(result);
        }
      } catch (e) {
        console.error('Experiments list fetch error:', e);
      }
    };
    fetchExperiments();
  }, []);

  // Fetch experiment impression report data when experiment_id or dateRange changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const baseUrl = `${window.nobid_endpoint}/experiment_impression_report`;
        const token = localStorage.getItem('token');
        let url = `${baseUrl}?show=experiment&show=date`;
        if (startDate && endDate) {
          url += `&start_date=${startDate}&end_date=${endDate}`;
        } else {
          url += `&date_range=${dateRange}`;
        }
        if (selectedExperimentId) {
          url += `&experiment_id=${selectedExperimentId}`;
        }
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const result = await response.json();
        if (result && result.data) {
          setData(result.data);
          setSummary(computeSummary(result.data));
        } else {
          setData([]);
          setSummary(null);
        }
      } catch (e) {
        console.error('Experiment report fetch error:', e);
        setData([]);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedExperimentId, dateRange, startDate, endDate, refreshKey]);

  const computeSummary = (records) => {
    const groups = {};
    const daily = {};

    records.forEach(rec => {
      const group = rec.experiment_group || 'unknown';
      if (!groups[group]) {
        groups[group] = { ad_requests: 0, bid_requests: 0, bid_responses: 0, impressions: 0, revenue: 0, net_revenue: 0 };
      }
      groups[group].ad_requests += rec.ad_requests || 0;
      groups[group].bid_requests += rec.bid_requests || 0;
      groups[group].bid_responses += rec.bid_responses || 0;
      groups[group].impressions += rec.impressions || 0;
      groups[group].revenue += rec.revenue || 0;
      groups[group].net_revenue += rec.net_revenue || 0;

      const date = rec.date;
      if (date) {
        if (!daily[date]) daily[date] = {};
        if (!daily[date][group]) daily[date][group] = { ad_requests: 0, bid_requests: 0, bid_responses: 0, impressions: 0, revenue: 0 };
        daily[date][group].ad_requests += rec.ad_requests || 0;
        daily[date][group].bid_requests += rec.bid_requests || 0;
        daily[date][group].bid_responses += rec.bid_responses || 0;
        daily[date][group].impressions += rec.impressions || 0;
        daily[date][group].revenue += rec.revenue || 0;
      }
    });

    const control = groups.control || { ad_requests: 0, bid_requests: 0, bid_responses: 0, impressions: 0, revenue: 0, net_revenue: 0 };

    // "treatment" = only the "test" group (promoted insights_ml floors)
    const treatment = groups.test || { ad_requests: 0, bid_requests: 0, bid_responses: 0, impressions: 0, revenue: 0, net_revenue: 0 };

    // Aggregate all bucket_* groups into "exploration"
    const exploration = { ad_requests: 0, bid_requests: 0, bid_responses: 0, impressions: 0, revenue: 0, net_revenue: 0 };
    Object.keys(groups).forEach(g => {
      if (!g.startsWith('bucket_')) return;
      exploration.ad_requests += groups[g].ad_requests;
      exploration.bid_requests += groups[g].bid_requests;
      exploration.bid_responses += groups[g].bid_responses;
      exploration.impressions += groups[g].impressions;
      exploration.revenue += groups[g].revenue;
      exploration.net_revenue += groups[g].net_revenue;
    });

    const controlRPR = control.bid_requests > 0 ? 1000000 * control.revenue / control.bid_requests : 0;
    const treatmentRPR = treatment.bid_requests > 0 ? 1000000 * treatment.revenue / treatment.bid_requests : 0;
    const rprLift = controlRPR > 0 ? (treatmentRPR - controlRPR) / controlRPR : 0;

    const controlCPM = control.impressions > 0 ? 1000 * control.revenue / control.impressions : 0;
    const treatmentCPM = treatment.impressions > 0 ? 1000 * treatment.revenue / treatment.impressions : 0;
    const cpmLift = controlCPM > 0 ? (treatmentCPM - controlCPM) / controlCPM : 0;

    const controlRequestsRate = control.bid_requests > 0 ? 100 * control.impressions / control.bid_requests : 0;
    const treatmentRequestsRate = treatment.bid_requests > 0 ? 100 * treatment.impressions / treatment.bid_requests : 0;

    // Sort: control first, test second, then buckets alphabetically
    const groupOrder = (g) => g === 'control' ? 0 : g === 'test' ? 1 : 2;
    const allGroups = Object.keys(groups).sort((a, b) => {
      const oa = groupOrder(a), ob = groupOrder(b);
      if (oa !== ob) return oa - ob;
      return a.localeCompare(b);
    });

    // Aggregated daily: control vs test (promoted floors) vs exploration (buckets)
    const dailyAgg = {};
    Object.keys(daily).forEach(date => {
      const dayGroups = daily[date];
      const c = dayGroups.control || { ad_requests: 0, bid_requests: 0, bid_responses: 0, revenue: 0, impressions: 0 };
      const t = dayGroups.test || { ad_requests: 0, bid_requests: 0, bid_responses: 0, revenue: 0, impressions: 0 };
      dailyAgg[date] = { control: c, test: t };
    });

    // Per-group stats for breakdown table
    const groupStats = allGroups.map(g => {
      const grp = groups[g];
      const rpmBR = grp.bid_requests > 0 ? 1000000 * grp.revenue / grp.bid_requests : 0;
      return {
        name: g,
        ad_requests: grp.ad_requests,
        bid_requests: grp.bid_requests,
        bid_responses: grp.bid_responses,
        response_rate: grp.bid_requests > 0 ? 100 * grp.bid_responses / grp.bid_requests : 0,
        impressions: grp.impressions,
        revenue: grp.revenue,
        rpmBR,
        cpm: grp.impressions > 0 ? 1000 * grp.revenue / grp.impressions : 0,
        fill_rate: grp.ad_requests > 0 ? 100 * grp.impressions / grp.ad_requests : 0,
        requests_rate: grp.bid_requests > 0 ? 100 * grp.impressions / grp.bid_requests : 0,
        lift: controlRPR > 0 && g !== 'control' ? (rpmBR - controlRPR) / controlRPR : null
      };
    });

    return {
      groups, control, treatment, exploration,
      controlRPR, treatmentRPR, rprLift,
      controlCPM, treatmentCPM, cpmLift,
      controlRequestsRate, treatmentRequestsRate,
      daily, dailyAgg, allGroups, groupStats
    };
  };

  const tableColumns = useMemo(() => {
    const controlRow = summary?.groupStats?.find(r => r.name === 'control');
    const selectedM = AGG_METRICS.find(x => x.value === aggMetric) || AGG_METRICS[0];
    const cl = (row, getValue) => metricLiftColor(row, controlRow, getValue);
    return [
      { name: 'Group', selector: row => row.name, sortable: true, cell: row => (
        <span style={{ fontWeight: row.name === 'control' ? 'bold' : 'normal' }}>{row.name}</span>
      )},
      { name: 'Ad Requests', selector: row => row.ad_requests, sortable: true, right: true, cell: row => long(row.ad_requests) },
      { name: 'Bid Requests', selector: row => row.bid_requests, sortable: true, right: true, cell: row => long(row.bid_requests) },
      { name: 'Bid Responses', selector: row => row.bid_responses, sortable: true, right: true, cell: row => long(row.bid_responses) },
      { name: 'Impressions', selector: row => row.impressions, sortable: true, right: true, cell: row => long(row.impressions) },
      { name: 'Revenue', selector: row => row.revenue, sortable: true, right: true, cell: row => formatDouble(row.revenue, '$', 2) },
      { name: 'Response Rate', selector: row => row.response_rate, sortable: true, right: true, cell: row => (
        <span style={{ color: cl(row, r => r.response_rate) }}>{`${row.response_rate.toFixed(2)}%`}</span>
      )},
      { name: 'Fill Rate', selector: row => row.fill_rate, sortable: true, right: true, cell: row => (
        <span style={{ color: cl(row, r => r.fill_rate) }}>{`${row.fill_rate.toFixed(2)}%`}</span>
      )},
      { name: 'RPM-BR', selector: row => row.rpmBR, sortable: true, right: true, cell: row => (
        <span style={{ color: cl(row, r => r.rpmBR), fontWeight: 'bold' }}>{formatDouble(row.rpmBR, '$', 2)}</span>
      )},
      { name: 'CPM', selector: row => row.cpm, sortable: true, right: true, cell: row => (
        <span style={{ color: cl(row, r => r.cpm) }}>{formatDouble(row.cpm, '$', 2)}</span>
      )},
      { name: 'Requests Rate', selector: row => row.requests_rate, sortable: true, right: true, cell: row => (
        <span style={{ color: cl(row, r => r.requests_rate) }}>{`${row.requests_rate.toFixed(2)}%`}</span>
      )},
      { name: `vs Control (${selectedM.label})`, selector: row => row.lift, sortable: true, right: true, cell: row => {
        const controlVal = controlRow ? selectedM.compute(controlRow) : 0;
        const rowVal = selectedM.compute(row);
        const lift = controlVal > 0 && row.name !== 'control' ? (rowVal - controlVal) / controlVal : null;
        return (
          <span style={{ color: lift === null ? '#aaaaaa' : liftColor(lift), fontWeight: 'bold' }}>
            {lift === null ? '—' : liftLabel(lift)}
          </span>
        );
      }}
    ];
  }, [summary, aggMetric]);

  const rprChartConfig = useMemo(() => {
    if (!summary) return {};
    const m = AGG_METRICS.find(x => x.value === aggMetric) || AGG_METRICS[0];
    const prefix = m.format === '$' ? '$' : '';
    const suffix = m.suffix || '';
    const controlVal = m.compute(summary.control);
    const controlLabel = m.decimals > 0 ? `${prefix}${controlVal.toFixed(m.decimals)}${suffix}` : `${prefix}${Math.round(controlVal).toLocaleString()}${suffix}`;
    const tooltipFmt = m.decimals > 0
      ? `<b>${prefix}{point.y:.${m.decimals}f}${suffix}</b>`
      : `<b>${prefix}{point.y:,.0f}${suffix}</b>`;
    let bucketIdx = 0;
    return {
      chart: { type: 'column', height: 280, backgroundColor: 'none', borderColor: BORDER_COLOR, borderWidth: 1 },
      title: { text: `${m.label} by Group`, style: { color: '#ffffff', fontSize: '12px', fontFamily: FONT_FAMILY } },
      xAxis: {
        categories: summary.allGroups,
        labels: { style: { color: '#ffffff', fontFamily: FONT_FAMILY } },
        crosshair: true
      },
      yAxis: {
        title: { text: m.label, style: { color: '#ffffff', fontFamily: FONT_FAMILY } },
        labels: { style: { color: '#ffffff', fontFamily: FONT_FAMILY } },
        plotLines: controlVal > 0 ? [{
          value: controlVal,
          color: '#e57373',
          dashStyle: 'Dash',
          width: 2,
          label: { text: `Control: ${controlLabel}`, style: { color: '#e57373', fontSize: '11px' } }
        }] : []
      },
      series: [{
        name: m.label,
        data: summary.allGroups.map(g => {
          const grp = summary.groups[g];
          const color = getGroupColor(g, g === 'control' ? 0 : bucketIdx++);
          return { y: m.compute(grp), color };
        }),
        showInLegend: false
      }],
      tooltip: { pointFormat: tooltipFmt },
      credits: { enabled: false }
    };
  }, [summary, aggMetric]);

  const timeSeriesConfig = useMemo(() => {
    if (!summary || !summary.daily) return {};
    const m = AGG_METRICS.find(x => x.value === aggMetric) || AGG_METRICS[0];
    const prefix = m.format === '$' ? '$' : '';
    const suffix = m.suffix || '';
    const tooltipFmt = m.decimals > 0
      ? `{series.name}: <b>${prefix}{point.y:.${m.decimals}f}${suffix}</b><br/>`
      : `{series.name}: <b>${prefix}{point.y:,.0f}${suffix}</b><br/>`;
    const dates = Object.keys(summary.daily).sort();
    let bucketIdx = 0;
    const series = summary.allGroups.map(group => ({
      name: group,
      data: dates.map(d => {
        const day = summary.daily[d][group] || { bid_requests: 0, bid_responses: 0, impressions: 0, revenue: 0 };
        return m.compute(day);
      }),
      color: getGroupColor(group, group === 'control' ? 0 : bucketIdx++)
    }));

    return {
      chart: { type: 'line', height: 280, backgroundColor: 'none', borderColor: BORDER_COLOR, borderWidth: 1 },
      title: { text: `${m.label} Over Time (by Group)`, style: { color: '#ffffff', fontSize: '12px', fontFamily: FONT_FAMILY } },
      xAxis: { categories: dates, labels: { style: { color: '#ffffff', fontFamily: FONT_FAMILY }, rotation: -45 }, crosshair: true },
      yAxis: { title: { text: m.label, style: { color: '#ffffff', fontFamily: FONT_FAMILY } }, labels: { style: { color: '#ffffff', fontFamily: FONT_FAMILY } } },
      series: series,
      legend: { itemStyle: { color: '#ffffff', fontFamily: FONT_FAMILY } },
      tooltip: { shared: true, pointFormat: tooltipFmt },
      credits: { enabled: false }
    };
  }, [summary, aggMetric]);

  // Aggregated Test vs Control bar chart (metric-switchable)
  const aggBarConfig = useMemo(() => {
    if (!summary) return {};
    const m = AGG_METRICS.find(x => x.value === aggMetric) || AGG_METRICS[0];
    const controlVal = m.compute(summary.control);
    const testVal = m.compute(summary.treatment);
    const lift = controlVal > 0 ? (testVal - controlVal) / controlVal : 0;
    const prefix = m.format === '$' ? '$' : '';
    const suffix = m.suffix || '';
    const tooltipFmt = m.decimals > 0
      ? `<b>${prefix}{point.y:.${m.decimals}f}${suffix}</b>`
      : `<b>${prefix}{point.y:,.0f}${suffix}</b>`;
    return {
      chart: { type: 'column', height: 280, backgroundColor: 'none', borderColor: BORDER_COLOR, borderWidth: 1 },
      title: { text: `${m.label}: Test vs Control`, style: { color: '#ffffff', fontSize: '12px', fontFamily: FONT_FAMILY } },
      xAxis: { categories: ['Control', 'Test'], labels: { style: { color: '#ffffff', fontFamily: FONT_FAMILY } }, crosshair: true },
      yAxis: { title: { text: m.label, style: { color: '#ffffff', fontFamily: FONT_FAMILY } }, labels: { style: { color: '#ffffff', fontFamily: FONT_FAMILY } } },
      series: [{
        name: m.label,
        data: [
          { y: controlVal, color: CONTROL_COLOR },
          { y: testVal, color: lift >= 0 ? '#81c784' : '#e57373' }
        ],
        showInLegend: false
      }],
      tooltip: { pointFormat: tooltipFmt },
      credits: { enabled: false }
    };
  }, [summary, aggMetric]);

  // Aggregated Test vs Control time series (metric-switchable)
  const aggTimeSeriesConfig = useMemo(() => {
    if (!summary || !summary.dailyAgg) return {};
    const m = AGG_METRICS.find(x => x.value === aggMetric) || AGG_METRICS[0];
    const dates = Object.keys(summary.dailyAgg).sort();
    const controlData = dates.map(d => m.compute(summary.dailyAgg[d].control));
    const testData = dates.map(d => m.compute(summary.dailyAgg[d].test));
    const prefix = m.format === '$' ? '$' : '';
    const suffix = m.suffix || '';
    const tooltipFmt = m.decimals > 0
      ? `{series.name}: <b>${prefix}{point.y:.${m.decimals}f}${suffix}</b><br/>`
      : `{series.name}: <b>${prefix}{point.y:,.0f}${suffix}</b><br/>`;
    return {
      chart: { type: 'line', height: 280, backgroundColor: 'none', borderColor: BORDER_COLOR, borderWidth: 1 },
      title: { text: `${m.label} Over Time (Test vs Control)`, style: { color: '#ffffff', fontSize: '12px', fontFamily: FONT_FAMILY } },
      xAxis: { categories: dates, labels: { style: { color: '#ffffff', fontFamily: FONT_FAMILY }, rotation: -45 }, crosshair: true },
      yAxis: { title: { text: m.label, style: { color: '#ffffff', fontFamily: FONT_FAMILY } }, labels: { style: { color: '#ffffff', fontFamily: FONT_FAMILY } } },
      series: [
        { name: 'Control', data: controlData, color: CONTROL_COLOR },
        { name: 'Test', data: testData, color: '#4fc3f7' }
      ],
      legend: { itemStyle: { color: '#ffffff', fontFamily: FONT_FAMILY } },
      tooltip: { shared: true, pointFormat: tooltipFmt },
      credits: { enabled: false }
    };
  }, [summary, aggMetric]);

  const handleExperimentChange = (event) => {
    setSelectedExperimentId(event.target.value);
  };

  if (loading && experiments.length === 0) {
    return (
      <Card raised>
        <CardContent>
          <div style={{ color: '#ffffff' }}>Loading experiment data...</div>
        </CardContent>
      </Card>
    );
  }

  const selectedMetric = AGG_METRICS.find(x => x.value === aggMetric) || AGG_METRICS[0];
  const metricLift = summary ? (() => {
    const controlVal = selectedMetric.compute(summary.control);
    const testVal = selectedMetric.compute(summary.treatment);
    return controlVal > 0 ? (testVal - controlVal) / controlVal : 0;
  })() : 0;
  const verdict = summary ? (metricLift >= 0.02 ? 'WINNING' : metricLift <= -0.02 ? 'LOSING' : 'NEUTRAL') : null;
  const verdictColor = summary ? (metricLift >= 0.02 ? '#81c784' : metricLift <= -0.02 ? '#e57373' : '#ffb74d') : '#aaaaaa';

  return (
    <div>
      {/* Filter Card */}
      <Card raised>
        <CardContent>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
            <TextField
              select
              label="Experiment"
              value={selectedExperimentId}
              onChange={handleExperimentChange}
              style={{ minWidth: 300 }}
              InputLabelProps={{ style: { color: '#aaaaaa' } }}
              InputProps={{ style: { color: '#ffffff' } }}
              variant="outlined"
              size="small"
            >
              <MenuItem value="">All Experiments</MenuItem>
              {experiments.map(exp => (
                <MenuItem key={exp.code || exp._id} value={exp.code}>
                  {exp.name} (ID: {exp.code}) at {exp.pace}%
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Metric"
              value={aggMetric}
              onChange={(e) => setAggMetric(e.target.value)}
              style={{ minWidth: 160 }}
              InputLabelProps={{ style: { color: '#aaaaaa' } }}
              InputProps={{ style: { color: '#ffffff' } }}
              variant="outlined"
              size="small"
            >
              {AGG_METRICS.map(m => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </TextField>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={() => setRefreshKey(k => k + 1)}
                style={{ color: '#ffffff' }}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </div>
        </CardContent>
      </Card>

      {loading && (
        <>
          {SEPARATOR}
          <Card raised>
            <CardContent>
              <div style={{ color: '#ffffff' }}>Loading...</div>
            </CardContent>
          </Card>
        </>
      )}

      {!loading && (!data || data.length === 0) && (
        <>
          {SEPARATOR}
          <Card raised>
            <CardContent>
              <div style={{ color: '#aaaaaa' }}>No experiment data available for the selected filter.</div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Summary Cards */}
      {!loading && summary && (
        <>
          {SEPARATOR}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', width: '100%', overflow: 'hidden', paddingBottom: '10px' }}>
            <div style={{ width: '100%' }}>
              <Card style={{ height: '100%' }} raised>
                <CardContent style={{ textAlign: 'center', color: 'white' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold' }}>VERDICT ({selectedMetric.label})</div>
                  <p style={{ fontSize: '25px', margin: '5px 0 0 0', fontWeight: '600', color: verdictColor }}>{verdict}</p>
                </CardContent>
              </Card>
            </div>
            <div style={{ width: '100%' }}>
              <Card style={{ height: '100%' }} raised>
                <CardContent style={{ textAlign: 'center', color: 'white' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{selectedMetric.label} LIFT</div>
                  <p style={{ fontSize: '25px', margin: '5px 0 0 0', fontWeight: '600', color: liftColor(metricLift) }}>{liftLabel(metricLift)}</p>
                </CardContent>
              </Card>
            </div>
            <div style={{ width: '100%' }}>
              <Card style={{ height: '100%' }} raised>
                <CardContent style={{ textAlign: 'center', color: 'white' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold' }}>CPM LIFT</div>
                  <p style={{ fontSize: '25px', margin: '5px 0 0 0', fontWeight: '600', color: liftColor(summary.cpmLift) }}>{liftLabel(summary.cpmLift)}</p>
                </CardContent>
              </Card>
            </div>
            <div style={{ width: '100%' }}>
              <Card style={{ height: '100%' }} raised>
                <CardContent style={{ textAlign: 'center', color: 'white' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold' }}>CONTROL {selectedMetric.label}</div>
                  <p style={{ fontSize: '25px', margin: '5px 0 0 0', fontWeight: '600' }}>{selectedMetric.format === '$' ? formatDouble(selectedMetric.compute(summary.control), '$', selectedMetric.decimals) : `${selectedMetric.compute(summary.control).toLocaleString(undefined, { minimumFractionDigits: selectedMetric.decimals, maximumFractionDigits: selectedMetric.decimals })}${selectedMetric.suffix || ''}`}</p>
                </CardContent>
              </Card>
            </div>
            <div style={{ width: '100%' }}>
              <Card style={{ height: '100%' }} raised>
                <CardContent style={{ textAlign: 'center', color: 'white' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold' }}>TEST {selectedMetric.label}</div>
                  <p style={{ fontSize: '25px', margin: '5px 0 0 0', fontWeight: '600' }}>{selectedMetric.format === '$' ? formatDouble(selectedMetric.compute(summary.treatment), '$', selectedMetric.decimals) : `${selectedMetric.compute(summary.treatment).toLocaleString(undefined, { minimumFractionDigits: selectedMetric.decimals, maximumFractionDigits: selectedMetric.decimals })}${selectedMetric.suffix || ''}`}</p>
                </CardContent>
              </Card>
            </div>
            <div style={{ width: '100%' }}>
              <Card style={{ height: '100%' }} raised>
                <CardContent style={{ textAlign: 'center', color: 'white' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold' }}>TEST IMPRESSIONS</div>
                  <p style={{ fontSize: '25px', margin: '5px 0 0 0', fontWeight: '600' }}>{long(summary.treatment.impressions)}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Aggregated Test vs Control Charts */}
      {!loading && summary && (
        <>
          {SEPARATOR}
          <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
            <div style={{ flex: 1 }}>
              <Card raised>
                <ReactHighcharts key={`agg-bar-${aggMetric}`} config={aggBarConfig} isPureConfig={true} />
              </Card>
            </div>
            <div style={{ flex: 1 }}>
              <Card raised>
                <ReactHighcharts key={`agg-ts-${aggMetric}`} config={aggTimeSeriesConfig} isPureConfig={true} />
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Per-Group Charts */}
      {!loading && summary && (
        <>
          {SEPARATOR}
          <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
            <div style={{ flex: 1 }}>
              <Card raised>
                <ReactHighcharts key={`grp-bar-${aggMetric}`} config={rprChartConfig} isPureConfig={true} />
              </Card>
            </div>
            <div style={{ flex: 1 }}>
              <Card raised>
                <ReactHighcharts key={`grp-ts-${aggMetric}`} config={timeSeriesConfig} isPureConfig={true} />
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Breakdown Table */}
      {!loading && summary && (
        <>
          {SEPARATOR}
          <Card raised>
            <DataTable
              title="Experiment Breakdown"
              theme={window.nobid_table_theme()}
              columns={tableColumns}
              data={summary.groupStats}
              dense
              highlightOnHover
              conditionalRowStyles={conditionalRowStyles}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default ExperimentReportComponent;
